import {
  forwardRef,
  type ComponentRef,
  type ComponentPropsWithoutRef,
} from 'react';
import { NavLink } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { SidebarNavLabel } from './SidebarNavLabel';
import { useLayout } from '@/hooks/useLayout';
import { useGuardedNavigation } from '@/hooks/useGuardedNavigation';

interface SidebarNavItemProps extends ComponentPropsWithoutRef<typeof NavLink> {
  icon?: React.ReactNode;
  label?: string;
}

/**
 * SidebarNavItem - Individual navigation item in the sidebar
 *
 * Similar to NavigationTab but optimized for vertical sidebar layout.
 * Automatically styles itself based on the current URL.
 *
 * @example
 * <SidebarNavItem to="/dashboard/alerts">
 *   <Eye className="h-4 w-4" /> Alerts
 * </SidebarNavItem>
 */
export const SidebarNavItem = forwardRef<
  ComponentRef<typeof NavLink>,
  SidebarNavItemProps
>(({ className, children, end = true, icon, label, ...props }, ref) => {
  const navigate = useGuardedNavigation();

  const { sidebarCollapsed } = useLayout();
  const isCustomRender = icon && label;

  const handleOnClick = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    e.preventDefault();
    navigate(props.to);
  };

  return (
    <NavLink
      {...props}
      onClick={handleOnClick}
      ref={ref}
      end={end}
      className={({ isActive }) => {
        const baseClasses = cn(
          'flex items-center min-w-0 rounded-lg gap-3 px-3 py-2 w-full box-border text-sm font-medium transition-all border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          sidebarCollapsed ? 'w-fit gap-0' : ''
        );
        const stateClasses = isActive
          ? 'bg-accent text-accent-foreground border-accent-border hover:bg-accent hover:text-accent-foreground'
          : 'text-muted-foreground border-transparent hover:bg-accent-hover hover:text-accent-hover-foreground';

        return cn(baseClasses, stateClasses, className);
      }}
    >
      {isCustomRender ? (
        <>
          {icon}
          <SidebarNavLabel label={label ?? ''} collapsed={sidebarCollapsed} />
        </>
      ) : (
        children
      )}
    </NavLink>
  );
});
SidebarNavItem.displayName = 'SidebarNavItem';
